import React, { memo, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { isSuperUser } from '../../utils/checkPermissions';
import connectOptions, { mergeProps } from '../../utils/connectOptions';
import {
  loadBenchmarks,
  updateBenchmarks,
  changeRoleForm,
  changeTeamForm,
  resetForm,
  autofillBenchmarks,
} from '../../actions/directorDashboard';
import Button from '../../components/helpers/Button';
import DropDown, { DropDownElement } from '../../components/helpers/DropDown';
import { RoleLevelBenchmarksTable } from '../../components/DirectorDashboard/Benchmarks/RoleLevelBenchmarksTable';
import { TeamLevelBenchmarksTable } from '../../components/DirectorDashboard/Benchmarks/TeamLevelBenchmarksTable';
import { usePopupContext } from '../../contexts/PopupContext';

import style from './style.module.css';

const BenchmarksContainer = ({
  benchmarks,
  benchmarksLoading,
  user,
  loadBenchmarks,
  updateBenchmarks,
  changeRoleForm,
  changeTeamForm,
  resetForm,
  autofillBenchmarks,
}) => {
  const { openPopup, closePopup } = usePopupContext();
  const [isEditState, setEditState] = useState(false);
  const [isChanged, setChanged] = useState(false);

  useEffect(() => {
    loadBenchmarks();
  }, []);

  useEffect(() => {
    setEditState(benchmarks?.get('canEdit'));
  }, [benchmarks]);

  const userNames = `${user?.get('firstName')} ${user?.get('lastName')}`;
  const userRoleName = user?.getIn(['roles', 0, 'name'], '');
  const isSuperuser = isSuperUser(user?.getIn(['roles', 0, 'slug'], null));

  const handleDiscardClick = () => {
    openPopup('ConfirmPopup', {
      header: 'Discard changes',
      message: 'Are you sure you want to discard your changes?',
      yes: 'Confirm',
      no: 'Cancel',
      onOk: () => {
        setChanged(false);
        resetForm();
        closePopup();
      },
      onCancel: () => closePopup(),
    });
  };

  const handleSaveClick = () => {
    updateBenchmarks({
      benchmarks,
      afterSuccess: () => {
        loadBenchmarks();
        setChanged(false);
      },
    });
  };

  const handleChangeRoleValue = (id, key, value) => {
    setChanged(true);
    changeRoleForm({ id, key, value });
  };

  const handleAutofillChange = useCallback(
    event => {
      event.preventDefault();
      setChanged(true);
      autofillBenchmarks(event.target.value);
    },
    [autofillBenchmarks],
  );

  const handleAutofillClick = useCallback(() => {
    setChanged(true);
    autofillBenchmarks('avg');
  }, [autofillBenchmarks]);

  const handleChangeTeamValue = (id, key, value) => {
    setChanged(true);
    changeTeamForm({ id, key, value });
  };

  return (
    <>
      <div className="row d-flex align-items-center pt10">
        <div className="col-md-9">
          <h3 className="text-primary-dark text-bold">Benchmarks</h3>
          <h6>Set weekly KPI targets by role and by team. Edit privileges are role-restricted; everyone else views.</h6>
        </div>
        <div className="col-md-3 d-flex justify-content-end">
          {isEditState && isSuperuser && (
            <Button className="btn-primary" onClick={handleAutofillClick}>
              Autofill benchmarks
            </Button>
          )}
          {isEditState && !isSuperuser && (
            <DropDown
              align="right"
              buttonContent="Autofill benchmarks"
              className="btn-primary"
              id="autofillBenchmarksDropDown"
              name="autofillBenchmarks"
              onChange={handleAutofillChange}
            >
              <DropDownElement className="text-decoration-none" value="firmWideBenchmark">
                Firm-wide averages
              </DropDownElement>
              <DropDownElement className="text-decoration-none" value="moduleAvg">
                Module-level averages
              </DropDownElement>
            </DropDown>
          )}
        </div>
      </div>
      <div className="row mt5">
        <div className="col-md-12">
          <div className="alert alert-info">
            {isEditState ? (
              <>
                You are signed in as <b>&nbsp;{userNames}&nbsp;</b> with <b>&nbsp;Benchmark {userRoleName}&nbsp;</b>{' '}
                privileges. Values in this tab are editable. Other team members will see a read-only view.
              </>
            ) : (
              <>
                You are signed in as <b>&nbsp;{userNames}&nbsp;</b>with Read-Only privileges. Values in this tab are not
                editable.
              </>
            )}
          </div>
        </div>
      </div>
      <div className="panel panel-default">
        <div className="panel-heading">
          <h5 className={style.panelHeading}>
            <span className="d-flex align-items-center flex-1">
              Role-Level Benchmarks
              <span className={style.hint}>
                Weekly targets by title · applied {isSuperuser ? 'firm-wide' : 'at the module level'}
              </span>
            </span>
            {!isSuperuser && (
              <span className={style.legend}>
                <span>
                  <span className={style.legendFirmWide} />
                  Firm-wide
                </span>
                <span>
                  <span className={style.legendModule} />
                  Module-level
                </span>
              </span>
            )}
          </h5>
        </div>
        <RoleLevelBenchmarksTable
          benchmarks={benchmarks}
          isEditState={isEditState}
          isLoading={benchmarksLoading}
          isSuperuser={isSuperuser}
          onChange={handleChangeRoleValue}
        />
      </div>

      <div className="panel panel-default hidden">
        <div className="panel-heading">
          <h5 className={style.panelHeading}>
            <span className="d-flex align-items-center flex-1">
              Team-Level Approach Benchmarks
              <span className={style.hint}>Weekly Approach targets per Team · roll up to Module total</span>
            </span>
            <span className={style.hint}>
              Each cell shows <b>Target</b> with avgactual for the last 8 weeks.
            </span>
          </h5>
        </div>
        <TeamLevelBenchmarksTable
          benchmarks={benchmarks}
          isEditState={isEditState}
          isLoading={benchmarksLoading}
          onChange={handleChangeTeamValue}
        />
      </div>
      {isEditState && (
        <div className="d-flex justify-content-end mb20">
          <Button className="btn-default mr5" disabled={!isChanged} onClick={handleDiscardClick}>
            Discard
          </Button>
          <Button className="btn-primary ml5" disabled={!isChanged} onClick={handleSaveClick}>
            Save
          </Button>
        </div>
      )}
    </>
  );
};

function mapStateToProps(state) {
  return {
    benchmarks: state.directorDashboard.get('benchmarks'),
    benchmarksLoading: state.directorDashboard.get('benchmarksLoading'),
    user: state.auth.get('user', null),
  };
}

export { BenchmarksContainer };
export default connect(
  mapStateToProps,
  {
    loadBenchmarks,
    updateBenchmarks,
    changeRoleForm,
    changeTeamForm,
    resetForm,
    autofillBenchmarks,
  },
  mergeProps,
  connectOptions,
)(memo(BenchmarksContainer));
